import { Inject, inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  cloneDeep,
  divide,
  filter,
  find,
  get,
  includes,
  isEqual,
  isObject,
  map,
  mapValues,
  multiply,
  round,
  set,
  sortBy,
  sum,
  toNumber,
  transform,
  values,
} from 'lodash';
import {
  BehaviorSubject,
  delay,
  filter as rxFilter,
  first,
  map as rxMap,
  Observable,
  startWith,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs';

import {
  ApplicationStepState,
  Contact,
  ContactCreateUpdate,
} from '@lockton-apps/shared/interfaces';
import {
  DynamicFormHooks,
  DynamicFormParsers,
  DynamicFormsExtensionsService,
} from '@lockton-apps/dynamic-forms';
import {
  LsosContactActions,
  LsosRenewalsApplicationActions,
} from '@lockton-apps/lsos-renewals/action';
import { ContactsApiActions } from '@lockton-apps/shared/action/contacts';
import { ContactCreateDialogComponent } from '@lockton-apps/shared/ui/contact-create-dialog';
import { PROPOSAL_PARTNER_DATA } from '../../constants/proposal-partner-data';
import { FormlySelectOption } from '@ngx-formly/core/select/select-options.pipe';

type PrincipalValue = {
  name: string;
  status: string;
  qualification: string;
  startDate: string;
  endDate: string | null;
};

type SelectOption = {
  label: string;
  value: unknown;
};

@Injectable()
export class LsosDynamicFormsExtensionsDeprecatedService extends DynamicFormsExtensionsService {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  protected override readonly dialog = inject(MatDialog);

  constructor(
    @Inject(PROPOSAL_PARTNER_DATA)
    protected readonly partnerData$: Observable<any>
  ) {
    super();
  }

  cachedFeeValue: any;

  private partnerOptions$ = new BehaviorSubject<SelectOption[]>([]);

  override readonly extensions = [
    {
      id: 'contactOptions',
      prop: 'options',
      value: this.partnerData$.pipe(
        rxMap(
          ({
            isPrimaryContact,
            contactsData: { contactsLoaded, allContacts },
            clientId,
          }) => {
            if (!contactsLoaded) {
              return [{ label: 'Loading...', value: null, disabled: true }];
            }

            return sortBy(
              transform(
                allContacts,
                (result: FormlySelectOption[], contact: Contact) => {
                  const userContactId: number | undefined = find(
                    contact.clients,
                    { id: clientId }
                  )?.userContactId;

                  if (userContactId) {
                    result.push({
                      label: contact.displayName,
                      value: userContactId,
                    });
                  }
                },
                isPrimaryContact
                  ? [{ label: 'Add a new contact', value: '' }]
                  : []
              ),
              'label'
            );
          }
        )
      ),
    },
    {
      id: 'partnerOptions',
      prop: 'options',
      value: this.partnerOptions$,
    },
  ];

  override readonly hooks: DynamicFormHooks[] = [
    {
      id: 'contactHook',
      hookConfig: {
        onInit: (field) => {
          let dialog: MatDialogRef<ContactCreateDialogComponent>;

          field.formControl?.valueChanges
            .pipe(
              rxFilter((v) => v === ''),
              withLatestFrom(this.partnerData$),
              tap(([, { clientId }]) => {
                field.formControl?.reset();

                dialog = this.dialog.open(ContactCreateDialogComponent, {
                  data: { clientId, showLanguageControl: false },
                });
              }),
              delay(0),
              switchMap(() => {
                return dialog.afterClosed().pipe(
                  rxFilter((result) => result),
                  switchMap((result) => this.addContact(result))
                );
              }),
              takeUntil(this.destroyNotifier)
            )
            .subscribe((action) => {
              field.formControl?.setValue(action.contact.userId);
            });
        },
      },
    },
    {
      id: 'partnersHook',
      hookConfig: {
        onInit: (field) => {
          field.formControl?.valueChanges
            .pipe(
              startWith(field.formControl.value),
              tap((value) => {
                const allPrincipals = value;

                const commonPrincipals = filter(
                  allPrincipals,
                  (principal) =>
                    isObject(principal) &&
                    'status' in principal &&
                    'name' in principal &&
                    principal.name &&
                    includes(['DP', 'CP'], principal.status)
                ) as PrincipalValue[];

                const options = map(commonPrincipals, (option) => ({
                  label: option.name,
                  value: option.name,
                }));

                this.partnerOptions$.next(options);
              }),
              takeUntil(this.destroyNotifier)
            )
            .subscribe();
        },
      },
    },
    {
      id: 'feeHook',
      hookConfig: {
        onInit: (field) => {
          this.cachedFeeValue = cloneDeep(
            get(field.model, 'feeIncomeSourceOfFees')
          );
        },
        onDestroy: (field) => {
          this.partnerData$.pipe(first()).subscribe((application) => {
            const model = cloneDeep(get(field.model, 'feeIncomeSourceOfFees'));
            if (isEqual(this.cachedFeeValue, model) || !application) {
              return;
            }
            const steps = map(application.steps, (step) => {
              return step.id !== 'areas-of-practice'
                ? step
                : { ...step, state: ApplicationStepState.Invalid };
            });

            this.store.dispatch(
              LsosRenewalsApplicationActions.updateStepsState({ steps })
            );
          });
        },
      },
    },
  ];

  override readonly parsers: DynamicFormParsers[] = [
    {
      id: 'breakdown',
      parsers: [
        (model: unknown) => {
          if (isObject(model)) {
            const propConv = toNumber(
              get(model, 'propertyConveyancing._declaredValue')
            );
            const breakdownTotal = sum(
              values(mapValues(model, '_declaredValue'))
            );

            const estateAgency = toNumber(
              get(model, 'propertyConveyancing.estateAgency')
            );

            const propConvPer = this.percent(propConv, breakdownTotal);

            const estateAgencyPercent = this.percent(estateAgency, propConv);

            set(model, 'derived.breakdownTotal', breakdownTotal);
            set(model, 'derived.propConvPer', propConvPer);
            set(model, 'derived.estateAgencyPer', estateAgencyPercent);
          }
          return model;
        },
      ],
    },
  ];

  addContact(contact: ContactCreateUpdate) {
    this.store.dispatch(LsosContactActions.addContact({ contact }));

    return this.actions$.pipe(ofType(ContactsApiActions.addSuccess), first());
  }

  private percent(value: number, total: number): number {
    return round(multiply(divide(value, total), 100), 2) || 0;
  }
}
